import { Box, Grid, LinearProgress, Divider, Paper } from '@mui/material';
import {
  DataGridProProps,
  GridColumnOrderChangeParams,
  GridColumnVisibilityModel,
  GridRowClassNameParams,
  GridSortModel,
  MuiEvent,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { DataGridPro as DataGrid, DataGridPro } from '@mui/x-data-grid-pro/DataGridPro/DataGridPro';
import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useSelector } from 'store/hooks';
import { useAppState } from 'store/Provider';
import { useColumnsParams } from './TodoTableService';
import { clearSelectedFilterAction, selectFilterAction, selectFilterRemoveAction } from 'store/actions/filterActions';

import { changePageSizeAction, changePageAction, sortDashboardTodoAction } from 'store/actions/dashboardActions';
import SearchHeaderCell from 'components/SearchHeaderCell/SearchHeaderCellBL';
import SearchHeaderCellAutoComplete from 'components/SearchHeaderCell/SearchAutocomplateHeaderCell';
import { useTranslation } from 'react-i18next';
import FilterDropdown from 'components/FilterDropdown';

type EditTotalWorkunitProps = {
  FilterTodo: any;
  dashboardGridData: any[];
  FilterNeeded: boolean;
  isLoading?: boolean;
  notaskImg?: string;
};

const TodoOrderTable = (props: EditTotalWorkunitProps) => {
  const { FilterTodo, dashboardGridData = [], isLoading: loadingProp } = props;
  const { filter, dashboard } = useSelector((state) => state);

  const { dashboard_orders, currentPage, pageSize } = dashboard;
  const { totalItems } = dashboard?.pageHandler;
  const { dispatch } = useAppState();
  const [t] = useTranslation();
  //  const order = useSelector((state) => state?.deliverable?.order);
  const apiRef = useGridApiRef();

  const [idsWithName, setIdsWithName] = useState<{ id: number; name: string }[]>([]);
  const [typeFilter, setTypeFilter] = useState<{ id: number; name: string }[]>([]);
  const [otplist, setOtplist] = useState<{ id: number; name: string }[]>([]);
  const [overdueBy, setOverdueBy] = useState<{ id: number; name: string }[]>([]);
  const [currentPageVal, setCurrentPageVal] = useState(currentPage);

  useEffect(() => {
    // idsWithName
    const ids =
      FilterTodo?.clients?.map((item: any) => ({
        id: item.id,
        name: item.name.toString(),
      })) || [];
    setIdsWithName(ids);

    // Type filter
    const typesData =
      FilterTodo?.types?.map((item: string) => ({
        id: item,
        name: item,
      })) || [];

    // setWorkunitScopes(uniqueScopes);
    setTypeFilter(typesData);

    // otplist
    const otp_list =
      FilterTodo?.otps?.map((item: any) => ({
        id: item.otp,
        name: item.otp,
      })) || [];

    setOtplist(otp_list);

    // Overdue By
    const overdueBy_list =
      FilterTodo?.overdueOptions?.map((item: string) => ({
        id: item,
        name: item,
      })) || [];

    setOverdueBy(overdueBy_list);
  }, [FilterTodo]);

  useEffect(() => {
    const selectedClient = Number(filter?.selected?.clients?.[0]);
    dispatch(selectFilterAction({ otp: [] }));
    const otp_list = (FilterTodo?.otps || []).reduce((acc: any, item: any) => {
      if (!selectedClient || item.customer_id === selectedClient) {
        acc.push({ id: item.otp, name: item.otp });
      }
      return acc;
    }, []);
    setOtplist(otp_list);
  }, [JSON.stringify(filter?.selected?.clients)]);

  useLayoutEffect(() => {
    dispatch(clearSelectedFilterAction());
    return () => {
      // Only drop the filters this table owns. A blanket clear here would also wipe the
      // filters ActionsCell injects for the deliverables page: React batches the navigate()
      // and the selectFilterAction() dispatches into a single flush, so this cleanup runs
      // after them and used to leave the destination page unfiltered.
      dispatch(selectFilterRemoveAction({ search: [], clients: [], type: [], otp: [], overdue_by: [] }));
    };
  }, []);

  const onPageSizeChange = (pageSize: number) => {
    dispatch(changePageSizeAction(pageSize));
    // dispatch(filterTotalFlagAction(true));
  };

  const onPageChange = (page: number) => {
    setCurrentPageVal(page);
    dispatch(changePageAction(Number(page)));
    // setTimeout(() => {
    //   dispatch(filterTotalFlagAction(true));
    // }, 500);
  };

  // useEffect(() => {
  //   // const handler = setTimeout(() => {
  //   console.log(currentPageVal);
  //   dispatch(changePageAction(Number(currentPageVal)));
  //   dispatch(filterTotalFlagAction(true));
  //   // }, 500);

  //   // return () => {
  //   //   clearTimeout(handler);
  //   // };
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [currentPageVal]);

  const onSortModelChange = React.useCallback((sortModel: GridSortModel) => {
    dispatch(sortDashboardTodoAction(sortModel));
    // dispatch(filterTotalFlagAction(true));
  }, []);

  const getRowClassName = useCallback((params: GridRowClassNameParams) => {
    if (params.row.label && params.row.label.includes('cancelled')) return 'Mui-disabled';
    return params.indexRelativeToCurrentPage % 2 === 0 ? 'Mui-even' : 'Mui-odd';
  }, []);

  const [filterButtonEl, setFilterButtonEl] = React.useState<HTMLButtonElement | null>(null);
  const columns = useColumnsParams(dashboard_orders ?? []);

  const dataTableProps: Omit<DataGridProProps, 'columns'> = {
    rows: dashboard_orders ?? [],
    // onSortModelChange,
    onPageChange,
  };

  return (
    <Paper
      elevation={1}
      sx={{
        width: '100%',
        borderRadius: '20px',
        overflow: 'hidden',
        boxShadow: '0px 10px 30px rgba(13, 58, 116, 0.06)',
      }}
    >
      <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: 'background.paper' }}>
        {props.FilterNeeded && (
          <Grid container spacing={2} sx={{ alignItems: 'center' }}>
            <Grid item xs={12} sm={6} md={2.4}>
              <SearchHeaderCell field="search" headerName="" disabled={loadingProp} />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <SearchHeaderCell
                field="clients"
                headerName={t('Customer')}
                options={idsWithName}
                disabled={loadingProp}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <FilterDropdown field="type" headerName={t('Type')} options={typeFilter} disabled={loadingProp} />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <SearchHeaderCellAutoComplete
                field="otp"
                headerName={t('OTP')}
                options={otplist}
                disabled={loadingProp}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <SearchHeaderCell
                field="overdue_by"
                headerName={t('Overdue_by')}
                options={overdueBy}
                disabled={loadingProp}
              />
            </Grid>
          </Grid>
        )}
      </Box>
      <Divider sx={{ borderColor: '#E9E9E9' }} />
      <Box
        sx={{
          height: '65vh',
          overflow: 'auto',
          scrollbarWidth: 'none', // Firefox
          '&::-webkit-scrollbar': {
            display: 'none', // Chrome, Safari, Edge
          },
        }}
      >
        <DataGridPro
          //   initialState={{
          //     pinnedColumns: { left: [GRID_CHECKBOX_SELECTION_COL_DEF.field, 'id', 'tags', 'workunit_name'] },
          //   }}
          columns={columns}
          components={{
            // Toolbar: CustomToolbar,
            LoadingOverlay: LinearProgress,
          }}
          componentsProps={{
            loadingOverlay: { style: { zIndex: 9999 } },
            panel: {
              anchorEl: filterButtonEl,
              placement: 'bottom-end',
            },
            toolbar: {
              setFilterButtonEl,
            },
          }}
          loading={loadingProp}
          // className={selectedBLAllDeliverables ? 'test' : 'test'}
          {...dataTableProps}
          apiRef={apiRef}
          autoHeight={false}
          pagination
          disableColumnMenu
          headerHeight={80}
          rowHeight={76}
          onPageSizeChange={onPageSizeChange}
          pageSize={pageSize}
          rowsPerPageOptions={[10, 25, 50, 100, 200]}
          page={currentPageVal}
          paginationMode="server"
          sortingMode="server"
          keepNonExistentRowsSelected
          disableSelectionOnClick
          rowCount={totalItems}
          //   selectionModel={selectedBLDeliverablesModel}
          // onPageChange={onPageChange}
          //   onSortModelChange={onSortModelChange}
          //   onSelectionModelChange={onSelectionModelChange}
          getRowClassName={getRowClassName}
          // checkboxSelection: !state.selectedAllDeliverables, // TODO ignore unselect or select after a select all
          //   checkboxSelection
          sx={{
            '& .Mui-disabled': {
              background: '#e1e5ef',
              opacity: '.9',
              pointerEvents: 'initial',
            },
            overflow: 'auto',
            mb: { xs: 30, md: 5 },
            '& .MuiDataGrid-columnHeader': {
              fontSize: '18px',
              fontWeight: 600,
              lineHeight: '1.4',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontSize: '15px',
              fontWeight: 500,
            },
            '& .MuiDataGrid-cell': {
              fontSize: '0.98rem',
            },
            '& .MuiDataGrid-columnHeader:focus': {
              outline: 'none',
              border: 'none',
            },
            '& .MuiDataGrid-columnHeader:focus-within': {
              outline: 'none',
              border: 'none',
            },
            '& .MuiDataGrid-columnHeader:active': {
              outline: 'none',
              border: 'none',
            },

            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
              border: 'none',
            },
            '& .MuiDataGrid-cell:focus-within': {
              outline: 'none',
              border: 'none',
            },
            '& .MuiDataGrid-cell:active': {
              outline: 'none',
              border: 'none',
            },
            '& .MuiDataGrid-columnHeaderTitleContainer .MuiDataGrid-sortIcon': {
              display: 'none !important',
            },

            '& .MuiDataGrid-row': {
              border: '1px solid #e9e9e9',
            },
          }}
        />
      </Box>
    </Paper>
  );
};

export default TodoOrderTable;
